"use client";
import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { PRODUCTS, Product, getStockLabel, formatPrice } from '@/lib/store';

export interface Review {
  id: string;
  productId: string;
  customerName: string;
  rating: number;
  comment: string;
  createdAt: string;
  approved: boolean;
}

const REVIEWS_KEY = 'cakepalace_reviews';

function StarRating({ rating, interactive = false, onRate }: { rating: number; interactive?: boolean; onRate?: (r: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type={interactive ? 'button' : 'button'}
          disabled={!interactive}
          onClick={() => interactive && onRate?.(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
        >
          <svg
            className={`w-5 h-5 transition-colors ${
              star <= (hovered || rating) ? 'text-amber-400' : 'text-gray-200'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        </button>
      ))}
    </div>
  );
}

function ProductDetailContent() {
  const searchParams = useSearchParams();
  const id = searchParams.get('id') || '1';
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewForm, setReviewForm] = useState({ customerName: '', rating: 0, comment: '' });
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    const found = PRODUCTS.find(p => p.id === id);
    setProduct(found || PRODUCTS[0]);
    setQuantity(1);
    setAdded(false);
    setReviewSubmitted(false);
    setReviewForm({ customerName: '', rating: 0, comment: '' });
  }, [id]);

  useEffect(() => {
    try {
      const all: Review[] = JSON.parse(localStorage.getItem(REVIEWS_KEY) || '[]');
      setReviews(all);
    } catch { /* no-op */ }
  }, [id]);

  if (!product) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const stockInfo = getStockLabel(product.stock);
  const relatedProducts = PRODUCTS.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAddToCart = () => {
    if (product.stock === 0) return;
    try {
      const cart = JSON.parse(localStorage.getItem('cakepalace_cart') || '[]');
      const existing = cart.find((i: { productId: string }) => i.productId === product.id);
      if (existing) {
        existing.quantity = Math.min(existing.quantity + quantity, product.stock);
      } else {
        cart.push({ productId: product.id, quantity, product });
      }
      localStorage.setItem('cakepalace_cart', JSON.stringify(cart));
      window.dispatchEvent(new Event('cartUpdated'));
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch (e) { console.error(e); }
  };

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    setReviewError('');
    if (!reviewForm.customerName.trim()) { setReviewError('Please enter your name.'); return; }
    if (reviewForm.rating === 0) { setReviewError('Please select a star rating.'); return; }
    if (!reviewForm.comment.trim()) { setReviewError('Please write a review comment.'); return; }
    if (reviewForm.comment.trim().length < 10) { setReviewError('Review must be at least 10 characters.'); return; }

    const newReview: Review = {
      id: Date.now().toString(),
      productId: product.id,
      customerName: reviewForm.customerName.trim(),
      rating: reviewForm.rating,
      comment: reviewForm.comment.trim(),
      createdAt: new Date().toISOString(),
      approved: false,
    };

    try {
      const all: Review[] = JSON.parse(localStorage.getItem(REVIEWS_KEY) || '[]');
      all.push(newReview);
      localStorage.setItem(REVIEWS_KEY, JSON.stringify(all));
      setReviews(all);
    } catch { /* no-op */ }

    setReviewSubmitted(true);
    setReviewForm({ customerName: '', rating: 0, comment: '' });
  };

  // Only show approved reviews for this product
  const approvedReviews = reviews.filter(r => r.productId === product.id && r.approved);
  const avgRating = approvedReviews.length > 0
    ? approvedReviews.reduce((s, r) => s + r.rating, 0) / approvedReviews.length
    : 0;

  // Simulate multiple images using different crop params
  const images = [
    product.image,
    product.image.replace('w=600', 'w=601'),
    product.image.replace('w=600', 'w=602'),
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 max-w-container mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted mb-8">
          <Link href="/product-shop" className="hover:text-primary transition-colors">Shop</Link>
          <Icon name="ChevronRightIcon" size={14} />
          <span className="text-muted-light">{product.category}</span>
          <Icon name="ChevronRightIcon" size={14} />
          <span className="text-foreground font-medium line-clamp-1">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-16">
          {/* Images */}
          <div className="animate-fade-in">
            <div className="aspect-square rounded-md overflow-hidden bg-white border border-border mb-4 relative">
              <AppImage
                src={images[activeImg]}
                alt={product.name}
                fill
                priority
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-cover"
              />
              {product.stock === 0 && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <span className="bg-white text-danger font-bold px-4 py-2 rounded-sm text-sm">Out of Stock</span>
                </div>
              )}
            </div>
            {/* Thumbnail row */}
            <div className="flex gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-sm overflow-hidden border-2 transition-all ${
                    activeImg === i ? 'border-primary' : 'border-border hover:border-muted-light'
                  }`}
                >
                  <AppImage src={img} alt={`${product.name} view ${i + 1}`} width={80} height={80} className="object-cover w-full h-full" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-muted">{product.category}</span>
              <span className="text-muted-light">·</span>
              <span className="text-xs text-muted-light font-mono">SKU: {product.sku}</span>
            </div>

            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-4 leading-tight">
              {product.name}
            </h1>

            <div className="flex items-center gap-4 mb-4">
              <span className="font-display text-3xl font-semibold text-primary">{formatPrice(product.price)}</span>
              <span className={`badge-stock ${stockInfo.className} text-sm`}>{stockInfo.label}</span>
            </div>

            {/* Star summary */}
            {approvedReviews.length > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <StarRating rating={Math.round(avgRating)} />
                <span className="text-sm font-semibold text-foreground">{avgRating.toFixed(1)}</span>
                <span className="text-sm text-muted">({approvedReviews.length} review{approvedReviews.length !== 1 ? 's' : ''})</span>
              </div>
            )}

            <p className="text-muted leading-relaxed mb-8">{product.description}</p>

            {/* Stock indicator */}
            {product.stock > 0 && product.stock <= 10 && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-2.5 mb-6 text-sm">
                <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-600" />
                <span className="text-amber-700 font-medium">Only {product.stock} units remaining</span>
              </div>
            )}

            {/* Quantity + Add to Cart */}
            {product.stock > 0 ? (
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">Qty:</span>
                  <div className="flex items-center gap-2">
                    <button
                      className="qty-btn"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                    >
                      <Icon name="MinusIcon" size={16} />
                    </button>
                    <span className="w-10 text-center font-semibold text-foreground">{quantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                      disabled={quantity >= product.stock}
                    >
                      <Icon name="PlusIcon" size={16} />
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  className={`flex-1 py-3 text-xs sm:text-sm font-semibold rounded-sm flex items-center justify-center gap-2 transition-all duration-300 ${
                    added ? 'bg-success text-white' : 'btn-primary'
                  }`}
                >
                  {added ? (
                    <><Icon name="CheckCircleIcon" size={18} variant="solid" /> Added to Cart!</>
                  ) : (
                    <><Icon name="ShoppingCartIcon" size={18} /> <span className="hidden xs:inline">Add to Cart — </span>{formatPrice(product.price * quantity)}</>
                  )}
                </button>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-sm px-4 py-3 mb-8 text-sm text-danger font-medium">
                This product is currently out of stock.
              </div>
            )}

            {/* Proceed to cart */}
            {added && (
              <Link
                href="/cart"
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-medium border border-border rounded-sm text-foreground hover:bg-accent transition-colors animate-fade-in mb-6"
              >
                <Icon name="ShoppingCartIcon" size={16} />
                View Cart & Checkout
              </Link>
            )}

            {/* Features */}
            <div className="border-t border-border pt-6 space-y-3">
              {[
                { icon: 'TruckIcon', text: 'Free delivery in Accra on orders over ₵200' },
                { icon: 'ArrowPathIcon', text: 'Easy returns within 7 days' },
                { icon: 'ChatBubbleLeftEllipsisIcon', text: 'WhatsApp support: 024557827897' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-muted">
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary" />
                  {f.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── REVIEWS SECTION ── */}
        <section className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <h2 className="font-display text-2xl font-semibold text-foreground">
              Customer <span className="text-primary italic">Reviews</span>
            </h2>
            {approvedReviews.length > 0 && (
              <div className="flex items-center gap-2">
                <StarRating rating={Math.round(avgRating)} />
                <span className="text-sm font-semibold text-foreground">{avgRating.toFixed(1)}</span>
                <span className="text-sm text-muted">/ 5</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
            {/* Reviews list */}
            <div className="lg:col-span-3 space-y-4">
              {approvedReviews.length === 0 ? (
                <div className="bg-white border border-border rounded-md py-12 text-center">
                  <svg className="w-10 h-10 text-muted-light mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-sm font-medium text-foreground mb-1">No reviews yet</p>
                  <p className="text-xs text-muted">Be the first to review this product!</p>
                </div>
              ) : (
                approvedReviews
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map(review => (
                    <div key={review.id} className="bg-white border border-border rounded-md p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-primary">
                              {review.customerName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{review.customerName}</p>
                            <p className="text-xs text-muted">
                              {new Date(review.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <StarRating rating={review.rating} />
                      </div>
                      <p className="text-sm text-muted leading-relaxed">{review.comment}</p>
                    </div>
                  ))
              )}
            </div>

            {/* Submit review form */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-border rounded-md overflow-hidden lg:sticky lg:top-24">
                <div className="px-5 py-4 border-b border-border bg-accent/40">
                  <h3 className="font-display text-base font-semibold text-foreground">Write a Review</h3>
                  <p className="text-xs text-muted mt-0.5">Share your experience with this product</p>
                </div>

                {reviewSubmitted ? (
                  <div className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                      <Icon name="CheckCircleIcon" size={24} className="text-success" variant="solid" />
                    </div>
                    <p className="text-sm font-semibold text-foreground mb-1">Review Submitted!</p>
                    <p className="text-xs text-muted mb-4">Thank you for your feedback. Your review will appear once approved by our team.</p>
                    <button
                      onClick={() => setReviewSubmitted(false)}
                      className="text-xs text-primary hover:underline"
                    >
                      Write another review
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitReview} className="p-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Your Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        value={reviewForm.customerName}
                        onChange={e => setReviewForm(f => ({ ...f, customerName: e.target.value }))}
                        placeholder="e.g. Ama Owusu"
                        className="form-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Rating <span className="text-danger">*</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <StarRating
                          rating={reviewForm.rating}
                          interactive
                          onRate={r => setReviewForm(f => ({ ...f, rating: r }))}
                        />
                        {reviewForm.rating > 0 && (
                          <span className="text-xs text-muted">
                            {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][reviewForm.rating]}
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Review <span className="text-danger">*</span>
                      </label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                        placeholder="Tell others what you think about this product..."
                        rows={4}
                        className="form-input resize-none"
                      />
                      <p className="text-xs text-muted mt-1">{reviewForm.comment.length}/500 characters</p>
                    </div>

                    {reviewError && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2.5 text-sm text-danger">
                        <Icon name="ExclamationCircleIcon" size={16} />
                        {reviewError}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn-primary w-full py-2.5 text-sm flex items-center justify-center gap-2"
                    >
                      <Icon name="PaperAirplaneIcon" size={16} />
                      Submit Review
                    </button>

                    <p className="text-xs text-muted text-center flex items-center justify-center gap-1">
                      <Icon name="ShieldCheckIcon" size={12} />
                      Reviews are moderated before publishing
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-semibold text-foreground mb-6">
              More in <span className="text-primary italic">{product.category}</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {relatedProducts.map(rel => (
                <Link
                  key={rel.id}
                  href={`/product-detail?id=${rel.id}`}
                  className="product-card bg-white rounded-md overflow-hidden border border-border block"
                >
                  <div className="aspect-square overflow-hidden relative">
                    <AppImage src={rel.image} alt={rel.name} fill sizes="25vw" className="object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-foreground line-clamp-1 mb-1">{rel.name}</p>
                    <p className="text-sm font-bold text-primary">{formatPrice(rel.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default function ProductDetailPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <ProductDetailContent />
    </Suspense>
  );
}